import { useEffect, useState } from 'react'
import { getJwt } from '@/shared/lib/betterAuth/get-jwt'
import { downloadPetClaimDocument } from '@/services/claims.services'
import { saveBlob } from '@/shared/helpers/save-blob'
import type { DocumentPreviewKind } from '@/components/molecules'
import type { ClaimDocument } from '@/shared/types/global.types'

const PDF_CONTENT_TYPE = 'application/pdf'
// HEIC renders in Safari only, so it falls back to download elsewhere
const PREVIEWABLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic']

function getPreviewKind(contentType: string): DocumentPreviewKind {
  if (contentType === PDF_CONTENT_TYPE) return 'pdf'
  if (PREVIEWABLE_IMAGE_TYPES.includes(contentType)) return 'image'
  return 'unsupported'
}

// Claim files need the user's JWT, so they can't be linked directly: the file
// is fetched once through the proxy and shown from a local object URL
export function useClaimDocumentPreview(claimId: string) {
  const [document, setDocument] = useState<ClaimDocument | undefined>(undefined)
  const [blob, setBlob] = useState<Blob | undefined>(undefined)
  const [objectUrl, setObjectUrl] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  // e.g. HEIC outside Safari: the browser can't draw it
  const [hasRenderError, setHasRenderError] = useState(false)

  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [objectUrl])

  async function openPreview(target: ClaimDocument) {
    setDocument(target)
    setBlob(undefined)
    setObjectUrl(undefined)
    setHasError(false)
    setHasRenderError(false)
    setIsLoading(true)
    try {
      const token = await getJwt()
      if (!token) throw new Error('No JWT token')
      const file = await downloadPetClaimDocument(token, claimId, target.id)
      setBlob(file)
      setObjectUrl(URL.createObjectURL(file))
    } catch (error) {
      console.error(error)
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }

  function closePreview() {
    setDocument(undefined)
    setObjectUrl(undefined)
    setBlob(undefined)
  }

  function downloadPreview() {
    if (blob && document) saveBlob(blob, document.file_name)
  }

  const kind = document ? getPreviewKind(document.content_type) : 'unsupported'

  return {
    isOpen: !!document,
    fileName: document?.file_name,
    contentType: document?.content_type,
    objectUrl,
    kind: hasRenderError ? 'unsupported' : kind,
    isLoading,
    hasError,
    canDownload: !!blob,
    openPreview,
    onClose: closePreview,
    onDownload: downloadPreview,
    onRenderError: () => setHasRenderError(true),
  }
}
